import { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import PeriodBadge from "./PeriodBadge";

function stripPrefix(name) {
  return (name || "").replace(/^\s*#Printed\s*Tee\s*[-–:]\s*/i, "").trim();
}

function monthLabel(ym) {
  if (!ym) return "";
  try {
    const [y, m] = ym.split("-").map(Number);
    const monthName = new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "short" });
    return `${monthName} '${String(y).slice(-2)}`;
  } catch {
    return ym;
  }
}

const LEGS_STYLE = {
  "has legs": "text-emerald-700 border-emerald-700/40 bg-emerald-50/50",
  "steady fade": "text-neutral-700 border-neutral-500/40 bg-neutral-100/40",
  "one-hit": "text-[#c72c2c] border-[#c72c2c]/40 bg-[#c72c2c]/5",
};

function WeeklySparkline({ series, width = 180, height = 30 }) {
  if (!series?.length) {
    return <div className="w-[180px] h-[30px] bm-eyebrow text-neutral-300 flex items-center">no weeks</div>;
  }
  const maxOffset = series[series.length - 1].week_offset;
  const maxUnits = Math.max(1, ...series.map((s) => s.units));
  const pad = 2;
  // Fill any gap weeks (weeks with zero sales) so the line reflects real cadence
  const filled = [];
  for (let i = 0; i <= maxOffset; i++) {
    const found = series.find((s) => s.week_offset === i);
    filled.push(found || { week_offset: i, units: 0 });
  }
  const stepX = filled.length > 1 ? (width - pad * 2) / (filled.length - 1) : 0;
  const pts = filled.map((s, i) => {
    const x = pad + i * stepX;
    const y = height - pad - (s.units / maxUnits) * (height - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const line = pts.join(" ");
  const area = `${pad},${height - pad} ${line} ${(pad + (filled.length - 1) * stepX).toFixed(1)},${height - pad}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polygon points={area} fill="#0a0a0a" opacity="0.08" />
      <polyline points={line} fill="none" stroke="#0a0a0a" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
      {filled.map((s, i) => {
        if (s.units === 0) return null;
        const x = pad + i * stepX;
        const y = height - pad - (s.units / maxUnits) * (height - pad * 2);
        return <circle key={i} cx={x} cy={y} r="1.4" fill="#0a0a0a" />;
      })}
    </svg>
  );
}

function DesignRow({ d, index }) {
  const legsCls = LEGS_STYLE[d.legs_label] || "text-neutral-400 border-neutral-300";
  const weekCount = d.weekly_series?.length || 0;
  return (
    <tr className="border-b border-[#0a0a0a]/15" data-testid={`design-row-${index}`}>
      <td className="py-2 pr-3 align-top">
        <div className="font-medium">{stripPrefix(d.name)}</div>
        <div className="bm-eyebrow text-neutral-500 mt-0.5 font-mono-bm">
          Dropped {d.first_date || "—"} &middot; {d.months_since_drop}mo old
        </div>
      </td>
      <td className="py-2 px-3 text-right font-mono-bm tabular-nums font-semibold align-top">
        {d.total_units.toLocaleString()}
      </td>
      <td className="py-2 px-3 text-right font-mono-bm tabular-nums text-neutral-700 align-top" title={`M0: ${d.month_0_units}  M1: ${d.month_1_units}`}>
        {d.launch_baseline_units.toLocaleString()}
      </td>
      <td className="py-2 px-3 text-right font-mono-bm tabular-nums text-neutral-700 align-top">
        {d.month_3_units == null ? <span className="text-neutral-300">—</span> : d.month_3_units.toLocaleString()}
      </td>
      <td className="py-2 px-3 align-top">
        {d.retention_ratio == null ? (
          <div className="text-right bm-eyebrow text-neutral-400" title="Needs 3 months of history">too new</div>
        ) : (
          <>
            <div className="flex items-baseline justify-end gap-2">
              <span className="font-mono-bm tabular-nums text-sm font-semibold">{Math.round(d.retention_ratio * 100)}%</span>
              <span className={`bm-eyebrow inline-block px-1.5 py-0.5 border text-[9px] ${legsCls}`}>{d.legs_label}</span>
            </div>
            <div className="h-[2px] bg-[#0a0a0a]/10 mt-1">
              <div
                className={`h-full ${
                  d.legs_label === "has legs" ? "bg-emerald-700"
                  : d.legs_label === "steady fade" ? "bg-[#0a0a0a]"
                  : "bg-[#c72c2c]"
                }`}
                style={{ width: `${Math.min(100, d.retention_ratio * 100)}%` }}
              />
            </div>
          </>
        )}
      </td>
      <td className="py-2 px-3 align-top">
        <WeeklySparkline series={d.weekly_series} />
        <div className="bm-eyebrow text-neutral-500 mt-0.5 font-mono-bm">
          {weekCount}w of data
        </div>
      </td>
    </tr>
  );
}

function CohortBlock({ cohort }) {
  return (
    <div className="mb-6" data-testid={`cohort-${cohort.cohort_month}`}>
      <div className="flex items-baseline justify-between border-b border-[#0a0a0a] pb-2 mb-2">
        <div>
          <div className="bm-eyebrow text-neutral-500">Drop cohort</div>
          <h4 className="bm-headline text-2xl">{monthLabel(cohort.cohort_month)}.</h4>
        </div>
        <div className="bm-eyebrow text-neutral-500 font-mono-bm">
          {cohort.design_count} designs &middot; {cohort.total_units.toLocaleString()} lifetime units
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#0a0a0a]/40">
              <th className="text-left bm-eyebrow text-neutral-600 py-2 pr-3">Design</th>
              <th className="text-right bm-eyebrow text-neutral-600 py-2 px-3 w-20">Lifetime</th>
              <th className="text-right bm-eyebrow text-neutral-600 py-2 px-3 w-20" title="Launch peak = max of month 0 & 1">Launch</th>
              <th className="text-right bm-eyebrow text-neutral-600 py-2 px-3 w-20">M3</th>
              <th className="text-right bm-eyebrow text-neutral-600 py-2 px-3 w-32">Retention</th>
              <th className="text-left bm-eyebrow text-neutral-600 py-2 px-3">Weekly age curve</th>
            </tr>
          </thead>
          <tbody>
            {cohort.designs.map((d, i) => (
              <DesignRow key={d.name} d={d} index={`${cohort.cohort_month}-${i}`} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function TeeCohorts() {
  const [cohorts, setCohorts] = useState([]);
  const [legsWinners, setLegsWinners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get("/products/printed-tee-cohorts")
      .then((r) => {
        if (cancel) return;
        setCohorts(r.data?.cohorts || []);
        setLegsWinners(r.data?.legs_winners || []);
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  // Split cohorts: first 3 visible, rest ("this year") behind Show more
  const currentYear = new Date().getFullYear();
  const initial = cohorts.slice(0, 3);
  const restThisYear = cohorts.slice(3).filter((c) => c.cohort_month.startsWith(String(currentYear)) || c.cohort_month.startsWith(String(currentYear - 1)));
  const older = cohorts.slice(3).filter((c) => !c.cohort_month.startsWith(String(currentYear)) && !c.cohort_month.startsWith(String(currentYear - 1)));

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-6" data-testid="tee-cohorts">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
        <div className="bm-eyebrow">Table 12 &middot; Drop cohorts</div>
        <div className="bm-eyebrow text-neutral-500">Per-design read within each drop</div>
      </div>
      <h3 className="bm-headline text-3xl mb-1">Tee cohorts.</h3>
      <PeriodBadge label="All history, by drop week" note="each design's full life since first sale; not filtered by the date picker" className="mb-2" />
      <p className="bm-eyebrow text-neutral-500 mb-5">
        Every design's launch → M3 read plus its weekly age curve, grouped by drop month
      </p>

      {/* Legs Winners — prioritise for restocks & reprints */}
      {legsWinners.length > 0 && (
        <div className="mb-6 border border-emerald-800/40 bg-emerald-50/40 p-4" data-testid="legs-winners">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="bm-eyebrow text-emerald-800/80 mb-0.5">Table 13 &middot; Restock priorities</div>
              <h4 className="bm-headline text-xl">Legs winners.</h4>
              <p className="bm-eyebrow text-neutral-600 mt-0.5">
                Individual designs where month-3 sales beat the launch peak &middot; reprint these first
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {legsWinners.map((w, i) => (
              <div key={w.name} className="bg-[#f5f2eb] border border-emerald-800/40 p-3" data-testid={`legs-winner-${i}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="bm-eyebrow text-emerald-700">#{i + 1}</span>
                  <span className="font-mono-bm font-semibold text-emerald-800 tabular-nums text-lg">
                    {Math.round(w.retention_ratio * 100)}%
                  </span>
                </div>
                <div className="font-medium text-sm mt-1 truncate" title={w.name}>{stripPrefix(w.name)}</div>
                <div className="bm-eyebrow text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
                  <span>Drop {monthLabel(w.cohort_month)}</span>
                  <span>&middot;</span>
                  <span className="font-mono-bm">M3 {w.month_3_units} / launch {w.launch_baseline}</span>
                </div>
                <div className="bm-eyebrow text-neutral-500 mt-0.5 font-mono-bm">
                  {w.total_units.toLocaleString()} lifetime units &middot; {w.months_since_drop}mo old
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && cohorts.length === 0 && (
        <div className="text-sm text-neutral-500">Loading cohorts…</div>
      )}

      {!loading && cohorts.length === 0 && (
        <div className="text-sm text-neutral-600">No Printed Tee history yet.</div>
      )}

      {/* First 3 cohorts always visible */}
      {initial.map((c) => <CohortBlock key={c.cohort_month} cohort={c} />)}

      {/* Show more toggle */}
      {(restThisYear.length + older.length) > 0 && (
        <>
          <button
            onClick={() => setShowAll((v) => !v)}
            className="mt-2 mb-4 inline-flex items-center gap-1.5 bm-eyebrow border border-[#0a0a0a] px-3 py-1.5 hover:bg-[#0a0a0a]/5"
            data-testid="cohorts-show-more"
          >
            <ChevronDown size={13} className={`transition-transform ${showAll ? "rotate-180" : ""}`} />
            {showAll ? "Show less" : `Show older cohorts · ${restThisYear.length + older.length} more`}
          </button>
          {showAll && (
            <>
              {restThisYear.map((c) => <CohortBlock key={c.cohort_month} cohort={c} />)}
              {older.length > 0 && (
                <>
                  <div className="bm-eyebrow text-neutral-500 mt-4 mb-2 border-t border-[#0a0a0a]/20 pt-3">Prior years</div>
                  {older.map((c) => <CohortBlock key={c.cohort_month} cohort={c} />)}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
